import { useState, useCallback, useEffect } from 'react';
import { useDownloadTracking } from './useDownloadTracking';

interface Plugin {
  id: string;
  name: string;
  description: string;
  lastUpdated: string;
  color: string;
  wiki_url?: string;
  discord_url?: string;
  builds: {
    [key: string]: string[];
  };
}

interface PluginStats {
  [pluginId: string]: {
    average_rating: number;
    total_ratings: number;
    total_comments: number;
    rating_distribution: { [key: string]: number };
    downloads?: {
      total_downloads: number;
      downloads_today: number;
      downloads_this_week: number;
      downloads_this_month: number;
      unique_users: number;
    };
    total_downloads?: number;
  };
}

export const usePluginStatsManager = () => {
  const [pluginStats, setPluginStats] = useState<PluginStats>({});
  const [loadingStats, setLoadingStats] = useState(false);
  const { getEnhancedPluginStats } = useDownloadTracking();

  const loadStatsForPlugin = useCallback(async (pluginId: string) => {
    try {
      const stats = await getEnhancedPluginStats(pluginId);
      if (stats) {
        setPluginStats(prev => ({
          ...prev,
          [pluginId]: {
            ...stats,
            total_downloads: stats.downloads?.total_downloads || 0
          }
        }));
      }
      return stats;
    } catch (error) {
      console.error(`Failed to load stats for ${pluginId}:`, error);
      return null;
    }
  }, [getEnhancedPluginStats]);

  const loadAllPluginStats = useCallback(async (plugins: Plugin[]) => {
    if (plugins.length === 0) return;
    
    try {
      setLoadingStats(true);
      const statsPromises = plugins.map(async (plugin: Plugin) => {
        try {
          const stats = await getEnhancedPluginStats(plugin.id);
          return { pluginId: plugin.id, stats };
        } catch (error) {
          console.error(`Failed to load stats for ${plugin.id}:`, error);
          return { pluginId: plugin.id, stats: null };
        }
      });

      const results = await Promise.all(statsPromises);
      const statsObj: PluginStats = {};
      
      results.forEach(({ pluginId, stats }) => {
        if (stats) {
          statsObj[pluginId] = {
            ...stats,
            total_downloads: stats.downloads?.total_downloads || 0
          };
        }
      });

      setPluginStats(statsObj);
    } catch (error) {
      console.error('Error loading plugin stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [getEnhancedPluginStats]);

  const refreshPluginStats = useCallback(async (pluginId: string) => {
    // Refresh stats for a specific plugin after an action (download, rating, etc.)
    await loadStatsForPlugin(pluginId);
  }, [loadStatsForPlugin]);

  const refreshAllStats = useCallback(async (plugins: Plugin[]) => {
    // Refresh all plugin stats
    await loadAllPluginStats(plugins);
  }, [loadAllPluginStats]);

  return {
    pluginStats,
    loadingStats,
    loadStatsForPlugin,
    loadAllPluginStats,
    refreshPluginStats,
    refreshAllStats
  };
}; 